import type { IndividualPaymentParcelViewModel } from '@/shared/api/generated';

import type { ParcelOperation } from '../types/parcels';
import { EParcelOperation } from '../types/parcels';

/**
 * `EStatusPayment` — o campo `statusId` da linha da grid.
 *
 * Não existe enum gerado para ele: o spec o descreve só em prosa, na operação
 * `shared/parcel-enums`. Os valores vêm do mapeamento do legado.
 */
export const EParcelStatus = {
  WaitingIntegration: 0,
  Pending: 1,
  Pay: 2,
  Cancelled: 3,
  Renegotiation: 4,
  Refunded: 5,
} as const;

/**
 * `EFinancialGridParcelStatus` — o campo `status`, que o backend calcula do
 * status com o vencimento (daí ter `Overdue`, que `statusId` não tem).
 *
 * **Não confundir com {@link EParcelStatus}**: os dois numeram coisas diferentes
 * — `Cancelled` é 3 num e 5 no outro. As regras do legado usam um ou outro por
 * botão, e trocá-los inverte a condição em silêncio.
 */
export const EParcelGridStatus = {
  Paid: 1,
  Renegotiation: 2,
  Pending: 3,
  Overdue: 4,
  Cancelled: 5,
  Refunded: 6,
} as const;

/**
 * `EPaymentType` — o campo `paymentType` da linha da grid.
 *
 * Como os dois enums de status, o spec não o gera: os valores vêm do mapeamento
 * do legado, onde a condição de cada botão de cobrança é escrita em cima deles.
 */
export const EPaymentType = {
  Billet: 1,
  CreditCard: 2,
  CreditCardRecurrence: 3,
  BilletPecegePay: 6,
  CreditCardPecegePay: 7,
} as const;

/** Formas que geram boleto — regra do botão "Boleto" do legado. */
const BILLET_PAYMENT_TYPES: readonly number[] = [EPaymentType.Billet, EPaymentType.BilletPecegePay];

/** Formas de cartão — regra do botão "Enviar Cobrança" do legado. */
const CARD_PAYMENT_TYPES: readonly number[] = [
  EPaymentType.CreditCard,
  EPaymentType.CreditCardRecurrence,
  EPaymentType.CreditCardPecegePay,
];

/**
 * O contrato ou a parcela é cobrado em cartão?
 *
 * Exportado porque o cabeçalho do contrato também precisa: o último cartão só é
 * exibido em contrato de cartão.
 *
 * @param paymentType - `paymentType` da linha da grid ou do cabeçalho.
 */
export const isCardPaymentType = (paymentType: number | null | undefined): boolean =>
  paymentType != null && CARD_PAYMENT_TYPES.includes(paymentType);

/**
 * Formas que aceitam devolução.
 *
 * **Não é o mesmo conjunto de {@link CARD_PAYMENT_TYPES}**: o diálogo de
 * devolução do legado monta o bloco de cartão só em `3` e `7`, e `2` cai no ramo
 * "reembolso não permitido" junto do resto. Cartão comum (2) provavelmente é
 * lançamento sem transação no provedor para estornar.
 *
 * Boleto (1 e 6) tinha bloco próprio no legado, mas **não há endpoint de
 * devolução de boleto** em `/api/v1` — o único é o do cartão
 * (`PATCH /pecegepay/creditcard/{id}/refund`), e o save da parcela não aceita
 * `refundValue`/`refundDate`. Por isso boleto também fica fora daqui.
 */
const REFUNDABLE_PAYMENT_TYPES: readonly number[] = [
  EPaymentType.CreditCardRecurrence,
  EPaymentType.CreditCardPecegePay,
];

/** Papéis do operador que as regras do legado consultam. */
export interface ParcelViewer {
  /** `Secretary` não escreve: é o papel que esconde quase toda ação. */
  readonly isSecretary: boolean;
  /** `Master` é o único que reintegra boleto. */
  readonly isMaster: boolean;
}

/** A parcela está aberta — aguardando integração ou pendente? */
const isOpen = (parcel: IndividualPaymentParcelViewModel): boolean =>
  parcel.statusId === EParcelStatus.WaitingIntegration || parcel.statusId === EParcelStatus.Pending;

/**
 * A parcela ainda pode receber emissão antecipada de NF?
 *
 * Tronco comum das regras 13 e 14 do legado (FEALQ e Vanzolini), que só diferem
 * na flag de integração do parceiro. `status` aqui é o da grid — daí `Paid` ser
 * 1 e `Cancelled` ser 5, e não os valores de `statusId`.
 *
 * **`preventFuturePayment === false`, não `!== true`.** O legado testa
 * `PreventFuturePayment == false` sobre um `bool` do modelo dele; aqui o campo é
 * `boolean | null`, e `!== true` deixaria a ação aparecer quando o backend manda
 * `null` — uma parcela em que o legado não exibia nada. Exigir o `false` explícito
 * é o lado fail-closed: sem a informação, a ação não aparece.
 */
const acceptsFutureInvoice = (parcel: IndividualPaymentParcelViewModel): boolean =>
  parcel.status !== EParcelGridStatus.Paid &&
  parcel.status !== EParcelGridStatus.Cancelled &&
  parcel.invoiceNumber == null &&
  parcel.preventFuturePayment === false;

/**
 * A operação deve aparecer no menu desta parcela?
 *
 * As regras vêm do mapeamento dos botões do grid legado
 * (`IndividualPayment/Parcels`), onde cada item do dropdown tinha a própria
 * condição de render. Sem elas o menu oferecia as treze operações em toda linha,
 * inclusive as que o backend recusa — estornar uma parcela em aberto, ativar uma
 * que não está cancelada.
 *
 * **Onde o legado só bloqueia no clique, aqui a ação é escondida.** `cancel`
 * aparecia no legado para qualquer status ≠ cancelado e só ao clicar abria um
 * alerta recusando parcela paga ou renegociada; `sendEmail` era exibido sempre e
 * recusava três status. Item que só serve para dizer "não pode" é ruído: o
 * operador não deve escolher um caminho sem saída. As condições aqui são as do
 * legado **somadas** às que ele avaliava no clique.
 *
 * **Nenhuma operação fora das 15 do legado entra no menu por conta própria.**
 * `reintegrate` entra porque tem regra do legado, só num grid vizinho; as duas de
 * NF que o painel novo tinha e o legado não (reemitir e enviar arquivo) foram
 * removidas, e `cancelInvoice` fica escondida porque no legado o botão está
 * comentado.
 *
 * @param operation - Operação do menu da parcela.
 * @param parcel - Linha da grid, de onde saem status, forma de pagamento e flags.
 * @param viewer - Papéis do operador logado.
 * @param contractPaymentType - `paymentType` do **contrato**, do cabeçalho. Hoje
 *   só `generateBillet` o consulta, para o botão não contrariar a forma de
 *   pagamento que a tela exibe no topo. Omitido — enquanto o cabeçalho carrega,
 *   por exemplo — nenhuma restrição de contrato é aplicada, e vale só a regra da
 *   parcela.
 * @returns `true` quando o item deve ser renderizado.
 *
 * @example
 * ```ts
 * isParcelOperationAvailable('activate', parcel, { isSecretary: false, isMaster: true });
 * ```
 */
export function isParcelOperationAvailable(
  operation: ParcelOperation,
  parcel: IndividualPaymentParcelViewModel,
  viewer: ParcelViewer,
  contractPaymentType?: number | null | undefined,
): boolean {
  // As duas leituras do painel novo e as duas ações que o legado libera para
  // secretária ficam antes da barreira de papel.
  if (operation === EParcelOperation.Traceability || operation === EParcelOperation.OrderTracking) {
    return true;
  }

  // Regra 1 do legado: acessar a NF na prefeitura não tem condição de papel — é
  // leitura de um link que a própria linha carrega.
  if (operation === EParcelOperation.OpenInvoice) {
    return (
      (parcel.status === EParcelGridStatus.Paid || parcel.status === EParcelGridStatus.Refunded) &&
      parcel.invoiceNumber != null &&
      (parcel.linkNfeCityHall ?? '') !== ''
    );
  }

  // Regra 2: gerar boleto é a única ação de escrita que o legado deixa na mão da
  // secretária.
  //
  // O legado olhava só o `PaymentType` **da parcela**, e isso produz um caso que
  // confunde: contrato em cartão pode ter parcela avulsa em boleto, e aí o botão
  // aparecia numa tela cujo cabeçalho diz "Cartão de Crédito". A guarda de
  // contrato fecha isso — o que o operador lê no topo passa a valer.
  if (operation === EParcelOperation.GenerateBillet) {
    const contractAllows =
      contractPaymentType == null || BILLET_PAYMENT_TYPES.includes(contractPaymentType);

    return (
      contractAllows && BILLET_PAYMENT_TYPES.includes(parcel.paymentType ?? -1) && isOpen(parcel)
    );
  }

  if (viewer.isSecretary) return false;

  switch (operation) {
    // Regra 5. O legado ainda exige um papel financeiro (`isFinancial`,
    // `isCompanyManager`) ou acesso de leitura ao próprio módulo — e esse último
    // termo do OR é verdadeiro para qualquer um que consiga abrir esta tela, o
    // que torna a condição inteira equivalente a `!isSecretary`. O controller é
    // que recusa parcela já paga.
    case EParcelOperation.Pay:
    // Regra 7: sem condição de status.
    case EParcelOperation.Edit:
      return true;

    // Regra 4. O `SendBillet` do legado nunca foi exposto em `/api/v1`, então a
    // ação não tem como ser executada — some do menu até o endpoint existir. As
    // condições de status do legado (recusa cancelada, paga e renegociação)
    // ficam registradas aqui para quando isso mudar.
    case EParcelOperation.SendEmail:
      return false;

    // Regra 3 — o botão `fa fa-credit-card`, cujo `reprocessCreditCard` é
    // confirmado pelo diálogo "Pagamento Manual" do legado.
    case EParcelOperation.SendCharge:
      return (
        CARD_PAYMENT_TYPES.includes(parcel.paymentType ?? -1) &&
        isOpen(parcel) &&
        parcel.hasTransactionNumber === true
      );

    // Regra 10.
    case EParcelOperation.ReversePayment:
      return parcel.statusId === EParcelStatus.Pay;

    // Regra 11. Mesmo status da 10, mais a forma de pagamento: o diálogo do
    // legado monta bloco de cartão em 3/7, bloco de boleto em 1/6 e, em qualquer
    // outro valor, esconde o Confirmar e diz "reembolso não permitido". Como
    // boleto não tem endpoint de devolução, sobra o ramo de cartão — e o ramo
    // "não permitido" deixa de precisar existir: a ação simplesmente não aparece.
    case EParcelOperation.RefundPayment:
      return (
        parcel.statusId === EParcelStatus.Pay &&
        parcel.paymentType != null &&
        REFUNDABLE_PAYMENT_TYPES.includes(parcel.paymentType)
      );

    // Regra 8.
    case EParcelOperation.Activate:
      return parcel.statusId === EParcelStatus.Cancelled;

    // Regra 9. O legado exibia para tudo ≠ cancelado e recusava paga/renegociada
    // no clique; as três ficam fora daqui.
    case EParcelOperation.Cancel:
      return (
        parcel.statusId !== EParcelStatus.Cancelled &&
        parcel.statusId !== EParcelStatus.Pay &&
        parcel.statusId !== EParcelStatus.Renegotiation
      );

    // Regra 6.
    case EParcelOperation.ResetIntegration:
      return isOpen(parcel);

    case EParcelOperation.Reintegrate:
      // Fora das 15 do legado: a única regra para reintegrar está no grid de
      // boletos, e é esta. Aplicada também ao item do menu, que age sobre a
      // parcela.
      return (
        viewer.isMaster &&
        parcel.status === EParcelGridStatus.Paid &&
        parcel.fealqIntegrated === true
      );

    // Regra 13. `integVersion == "3"` do legado é AppSetting do servidor e não
    // chega ao frontend; `hasIntegrationFealq` por linha é o que dá para checar.
    case EParcelOperation.InvoiceFuturePayment:
      return acceptsFutureInvoice(parcel) && parcel.hasIntegrationFealq === true;

    // Regra 14: idêntica à 13, trocando o parceiro.
    case EParcelOperation.InvoiceFuturePaymentVanzolini:
      return acceptsFutureInvoice(parcel) && parcel.hasIntegrationVanzolini === true;

    // Regra 15. Não repete o tronco da 13/14: aproveitar parcela não olha NF
    // emitida nem `preventFuturePayment`, só o excedente do parceiro.
    case EParcelOperation.TakeAdvantageParcel:
      return (
        parcel.status !== EParcelGridStatus.Paid &&
        parcel.status !== EParcelGridStatus.Cancelled &&
        parcel.hasIntegrationVanzolini === true &&
        parcel.hasExcedentBilletVanzolini === true
      );

    // Regra 12: no legado o botão está comentado no Razor e **nunca** é exibido.
    // Mantido escondido por paridade.
    //
    // Há também um impedimento de dado, que vale registrar para quando a ação for
    // reativada: `invoiceNumber != null` não distingue NF ativa de NF já
    // cancelada. O status da nota vive em `IndividualPaymentInvoiceViewModel`
    // (`status`/`statusText`), que vem de `GET /parcels/{parcelId}/invoices` —
    // carregado sob demanda por parcela, não na linha da grid. Uma condição
    // correta depende de o backend expor esse status no
    // `IndividualPaymentParcelViewModel`.
    //
    // O modal (`ParcelActionConfirmModal`), a config e a mutation continuam
    // prontos: reativar é trocar este `false` pela condição.
    case EParcelOperation.CancelInvoice:
      return false;

    // Fora do menu: é ação por boleto, exposta como ícone na linha do boleto.
    case EParcelOperation.DownloadPdf:
      return false;
  }
}

/**
 * O ícone de reintegrar deve aparecer nesta linha de boleto?
 *
 * Regra do grid de detalhe nível 2 do legado, onde a célula renderizava `-` para
 * quem não fosse `Master`. `status` é o da **parcela**, não do boleto: o legado
 * consulta a parcela-pai.
 *
 * @param parcel - Parcela dona do boleto.
 * @param viewer - Papéis do operador logado.
 */
export function canReintegrateBillet(
  parcel: Pick<IndividualPaymentParcelViewModel, 'status' | 'fealqIntegrated'>,
  viewer: Pick<ParcelViewer, 'isMaster'>,
): boolean {
  return (
    viewer.isMaster && parcel.status === EParcelGridStatus.Paid && parcel.fealqIntegrated === true
  );
}
